import React, { useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  KeyRound,
  Mail,
  MessageSquare,
  Smartphone,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  Account,
  accountApi,
  accountErr,
  FactorType,
  SecurityInfo,
} from "../../api/account/accountApi";
import TotpSetupModal from "./TotpSetupModal";
import AddSecurityKeyModal from "./AddSecurityKeyModal";

interface TwoFactorSectionProps {
  account: Account;
  security: SecurityInfo;
  onChanged: () => void;
  onGoToContact: () => void;
}

interface MethodDef {
  type: FactorType;
  label: string;
  hint: string;
  icon: React.ReactNode;
}

const METHODS: MethodDef[] = [
  {
    type: "totp",
    label: "Authenticator app",
    hint: "Codes from Google Authenticator, Microsoft Authenticator, Authy or 1Password.",
    icon: <Smartphone size={18} />,
  },
  {
    type: "otp_sms",
    label: "SMS code",
    hint: "A one-time code texted to your verified phone number.",
    icon: <MessageSquare size={18} />,
  },
  {
    type: "otp_email",
    label: "Email code",
    hint: "A one-time code sent to your verified email address.",
    icon: <Mail size={18} />,
  },
  {
    type: "u2f",
    label: "Security key / passkey",
    hint: "A hardware key or your device's fingerprint / face unlock.",
    icon: <KeyRound size={18} />,
  },
];

/**
 * Second-factor management. Which methods appear is decided by the org's
 * Zitadel login policy (security.allowed_second_factors) — if the policy
 * allows none, we say so instead of showing buttons that would fail.
 *
 * Security keys are enrolled in-app too (authz-api registers them on the
 * dashboard's RP domain); when the server has no login service account yet
 * the row explains that instead of offering a button.
 */
const TwoFactorSection: React.FC<TwoFactorSectionProps> = ({
  account,
  security,
  onChanged,
  onGoToContact,
}) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [totpSetup, setTotpSetup] = useState(false);
  const [keySetup, setKeySetup] = useState(false);

  const enrolled = (t: FactorType) => security.factors.filter((f) => f.type === t && f.ready);
  const allowed = (t: FactorType) => security.allowed_second_factors.includes(t);
  const anyReady = security.factors.some((f) => f.ready);

  const run = async (key: string, fn: () => Promise<any>, ok: string) => {
    setBusy(key);
    try {
      await fn();
      toast.success(ok);
      onChanged();
    } catch (err) {
      toast.error(accountErr(err, "Action failed"));
    } finally {
      setBusy(null);
    }
  };

  const prerequisite = (t: FactorType): string | null => {
    if (t === "otp_sms" && !account.phone_verified)
      return account.phone ? "Verify your phone number first." : "Add a phone number first.";
    if (t === "otp_email" && !account.email_verified)
      return account.email ? "Verify your email first." : "Add an email address first.";
    return null;
  };

  const enable = (t: FactorType) => {
    if (t === "totp") return setTotpSetup(true);
    if (t === "u2f") return setKeySetup(true);
    if (t === "otp_sms") return run("otp_sms", () => accountApi.otpSmsAdd(), "SMS codes enabled");
    if (t === "otp_email") return run("otp_email", () => accountApi.otpEmailAdd(), "Email codes enabled");
  };

  const disable = (t: FactorType, id?: string | null) => {
    const name = METHODS.find((m) => m.type === t)?.label || t;
    if (!window.confirm(`Remove ${name}? You will no longer be asked for it at sign-in.`)) return;
    if (t === "totp") return run("totp", () => accountApi.totpRemove(), "Authenticator app removed");
    if (t === "otp_sms") return run("otp_sms", () => accountApi.otpSmsRemove(), "SMS codes removed");
    if (t === "otp_email") return run("otp_email", () => accountApi.otpEmailRemove(), "Email codes removed");
    if (t === "u2f" && id) return run(`u2f:${id}`, () => accountApi.u2fRemove(id), "Security key removed");
  };

  const visible = METHODS.filter((m) => allowed(m.type) || enrolled(m.type).length > 0);

  return (
    <div className="acct-card">
      <div className="acct-card__head">
        <div>
          <h3>Two-factor authentication</h3>
          <p>A second step at sign-in, on top of your password.</p>
        </div>
        {anyReady ? (
          <span className="acct-badge acct-badge--ok">
            <CheckCircle2 size={12} /> On
          </span>
        ) : (
          <span className="acct-badge acct-badge--warn">Off</span>
        )}
      </div>

      {security.force_mfa && !anyReady && (
        <div className="acct-notice acct-notice--warn" style={{ marginBottom: 14 }}>
          <AlertTriangle size={16} />
          <span>
            Your organisation requires two-factor authentication. Set up at
            least one method below.
          </span>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="acct-notice acct-notice--info">
          <Info size={16} />
          <span>
            Your organisation's sign-in policy does not allow any second factor
            yet. Ask your administrator to enable one (Authenticator app, SMS,
            Email or Security key) in Zitadel under Login Behaviour and Security.
          </span>
        </div>
      ) : (
        visible.map((m) => {
          const have = enrolled(m.type);
          const on = have.length > 0;
          const missing = prerequisite(m.type);
          const isBusy = busy === m.type;

          return (
            <div className="acct-row" key={m.type}>
              <div className="acct-row__main">
                <div className="acct-row__icon">{m.icon}</div>
                <div>
                  <div className="acct-row__label">
                    {m.label}
                    {on ? (
                      <span className="acct-badge acct-badge--ok">
                        <CheckCircle2 size={12} /> Enabled
                      </span>
                    ) : (
                      <span className="acct-badge acct-badge--muted">Not set up</span>
                    )}
                  </div>
                  <div className="acct-row__hint">{m.hint}</div>
                  {m.type === "u2f" && have.length > 0 && (
                    <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 13 }}>
                      {have.map((k) => (
                        <li key={k.id || k.name} style={{ marginBottom: 4 }}>
                          {k.name || "Security key"}{" "}
                          <button
                            className="acct-link"
                            disabled={busy === `u2f:${k.id}`}
                            onClick={() => disable("u2f", k.id)}
                            style={{ color: "#b91c1c", marginLeft: 6 }}
                          >
                            remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {!on && missing && m.type !== "u2f" && (
                    <div className="acct-row__hint" style={{ color: "#b45309" }}>
                      {missing}{" "}
                      <button className="acct-link" onClick={onGoToContact}>
                        Go to contact details
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="acct-row__actions">
                {m.type === "u2f" ? (
                  allowed("u2f") &&
                  (security.u2f_in_app ? (
                    <button
                      className="acct-btn acct-btn--small acct-btn--primary"
                      onClick={() => enable("u2f")}
                    >
                      {have.length ? "Add another key" : "Add security key"}
                    </button>
                  ) : (
                    <span className="acct-badge acct-badge--muted" title="Ask your administrator to enable in-app login">
                      Not available yet
                    </span>
                  ))
                ) : on ? (
                  <button
                    className="acct-btn acct-btn--small acct-btn--danger"
                    disabled={isBusy}
                    onClick={() => disable(m.type)}
                  >
                    {isBusy ? "Removing..." : "Remove"}
                  </button>
                ) : (
                  allowed(m.type) && (
                    <button
                      className="acct-btn acct-btn--small acct-btn--primary"
                      disabled={isBusy || !!missing}
                      onClick={() => enable(m.type)}
                    >
                      {isBusy ? "Enabling..." : "Set up"}
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })
      )}

      {keySetup && (
        <AddSecurityKeyModal
          onClose={() => setKeySetup(false)}
          onDone={() => {
            setKeySetup(false);
            onChanged();
          }}
        />
      )}

      {totpSetup && (
        <TotpSetupModal
          onClose={() => setTotpSetup(false)}
          onDone={() => {
            setTotpSetup(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
};

export default TwoFactorSection;
